import { useCallback, useEffect, useState } from 'react';
import { initialize as initializeSdk } from 'src/utilities/sdkInitialization';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import { FeatureFlag } from 'src/types/FeatureFlag';
import { getConfig } from 'src/utilities/config';
import { getBrandSettingsFromUrl } from 'src/utilities/sdk';

export const useSdkInitialization = (): { isSdkInitialized: boolean } => {
  const { value: grubApiCoreFlag, isLoading: isGrubApiCoreLoading } =
    useFeatureFlag(FeatureFlag.ENABLE_GRUB_API_CORE);
  const { value: cookieAuthFlag, isLoading: isCookieAuthLoading } =
    useFeatureFlag(FeatureFlag.ENABLE_COOKIE_BASED_AUTH);

  // Get feature flags from config for current brand
  const config = getConfig();
  const { brand } = getBrandSettingsFromUrl();
  const brandFeatures = config.sdkFeatures?.[brand];

  // Enable features only if:
  // 1. Feature flag is enabled (FeatureFlag.ENABLE_GRUB_API_CORE / ENABLE_COOKIE_BASED_AUTH)
  // 2. AND config allows it for this brand/environment
  const enableGrubApiCore = grubApiCoreFlag && brandFeatures?.enableGrubApiCore;
  const enableCookieAuthentication =
    cookieAuthFlag && brandFeatures?.enableCookieAuthentication;

  const [isSdkInitialized, setIsSdkInitialized] = useState(false);
  const [isInInitializing, setIsInInitializing] = useState(false);

  const configureSdk = useCallback(async (): Promise<void> => {
    try {
      setIsInInitializing(true);
      await initializeSdk({ enableGrubApiCore, enableCookieAuthentication });
    } finally {
      setIsSdkInitialized(true);
      setIsInInitializing(false);
    }
  }, [enableGrubApiCore, enableCookieAuthentication]);

  useEffect(() => {
    // Wait for variants to load before SDK initialization.
    const variantsLoaded = !isGrubApiCoreLoading && !isCookieAuthLoading;

    const sdkReadyToInitialize = !isSdkInitialized && !isInInitializing;
    const hasRequiredData = variantsLoaded && brandFeatures;

    if (sdkReadyToInitialize && hasRequiredData) {
      configureSdk();
    }
  }, [
    configureSdk,
    isSdkInitialized,
    isInInitializing,
    isGrubApiCoreLoading,
    isCookieAuthLoading,
    brandFeatures,
  ]);

  return { isSdkInitialized };
};
